import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { IAlertDataTextDashboardItemOptions } from 'src/app/components/alert-data-text-dashboard-item/alert-data-text-dashboard-item.component';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import {
    IMultipleSelectDashboardItem,
    IMultipleSelectDatasourceColumnState,
} from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import {
    IDashboardItemDataLoaderLink,
    IDashboardItemOptionsDataLoaderLink,
} from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import {
    EChartsHelper,
    EChartsMetric,
} from 'src/app/utils/echarts/echarts-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactory } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import {
    IToggleSettingDashboardItem,
    ToggleComponent,
} from '../common/items/setting/toggle/toggle.component';
import { RetentionByDayNoDataLoaderFactory } from '../common/retention/data-loaders/retention-by-day-no';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { BiVizDashboardCampaignsDatasource } from '../mopa-campaigns/sql-maps/biviz-dashboard-campaigns-sql-map';
import { RevenueARPUDataLoaderFactory } from '../mopa-sales/data-loaders/revenue-arpu';
import { RevenueDauDataLoaderFactory } from '../mopa-sales/data-loaders/revenue-dau';
import { MopaCampaignsChartsChartDocs } from './mopa-campaigns-charts.chart-documentation';
import { CampaignsMetricsByDateDataLoaderFactory } from './data-loaders/campaigns-metrics-by-date';
import { CampaignsMetricsByDayNoDataLoaderFactory } from './data-loaders/campaigns-metrics-by-day-no';
import { CampaignsNetworkWarningDataLoaderFactory } from './data-loaders/campaigns-network-warning';

@Component({
    selector: 'app-mopa-campaigns-charts',
    templateUrl: './mopa-campaigns-charts.component.html',
    styleUrls: ['./mopa-campaigns-charts.component.scss'],
    providers: [DashboardService, QueryContextService],
})
export class MopaCampaignsChartsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Campaigns';
    override DASHBOARD_ID = 'mopa-campaigns-charts';

    values = Object.values;

    private defaultGcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////
    // dashboard config:
    // active / all campaigns-charts
    // pln usd net/gross
    // select columns
    // revenue type
    // first open date
    // app store,country, traffic source, campaign name, campaign_adgroup, campaign_creative, campaign_network
    override filterDataLoaders = {
        'mopa.campaigns_charts.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.campaigns_charts.filter.firebase_app_store',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns_charts.filter.firebase_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.firebase_country_tier',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
        'mopa.campaigns_charts.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns_charts.filter.firebase_country',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.campaigns_charts.filter.attribution_traffic_source':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_traffic_source',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
        'mopa.campaigns_charts.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_network_name',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
        'mopa.campaigns_charts.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_campaign_name',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
                filterContext: true,
            }).create(),
        'mopa.campaigns_charts.filter.attribution_ad': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns_charts.filter.attribution_ad',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.campaigns_charts.filter.attribution_ad_length':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_ad_length',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_AD_LENGTH_S',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ATTRIBUTION_AD_LENGTH_S'
                ),
            }).create(),
        'mopa.campaigns_charts.filter.attribution_ad_size': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.campaigns_charts.filter.attribution_ad_size',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_SIZE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns_charts.filter.attribution_ad_aspect_ratio':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_ad_aspect_ratio',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_AD_ASPECT_RATIO',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
        'mopa.campaigns_charts.filter.attribution_ad_type': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.campaigns_charts.filter.attribution_ad_type',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_TYPE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns_charts.filter.attribution_ad_theme':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_ad_theme',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_AD_THEME',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
        // 'ATTRIBUTION_AD_VERSION'
        'mopa.campaigns_charts.filter.attribution_ad_version':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.attribution_ad_version',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_AD_VERSION',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
    };

    override chartDataLoaders = {
        'mopa.campaigns_charts.modify_time_campaigns_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.campaigns_charts.modify_time_campaigns_datasource',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: 'bi_viz',
                tableId: 'dashboard_campaigns',
            }).create(),
        'mopa.campaigns_charts.campaigns_network_warning':
            new CampaignsNetworkWarningDataLoaderFactory({
                createdInstanceId:
                    'mopa.campaigns_charts.campaigns_network_warning',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.campaigns_charts.revenue_dau': new RevenueDauDataLoaderFactory({
            createdInstanceId: 'mopa.campaigns_charts.revenue_dau',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.campaigns_charts.revenue_arpu': new RevenueARPUDataLoaderFactory({
            createdInstanceId: 'mopa.campaigns_charts.revenue_arpu',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.campaigns_charts.retention_by_day_no':
            new RetentionByDayNoDataLoaderFactory({
                createdInstanceId: 'mopa.campaigns_charts.retention_by_day_no',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.campaigns_charts.campaigns_metrics_by_date':
            new CampaignsMetricsByDateDataLoaderFactory({
                createdInstanceId:
                    'mopa.campaigns_charts.campaigns_metrics_by_date',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.campaigns_charts.campaigns_metrics_by_day_no':
            new CampaignsMetricsByDayNoDataLoaderFactory({
                createdInstanceId:
                    'mopa.campaigns_charts.campaigns_metrics_by_day_no',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    // settingItemDateAggregation: ISettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.DATE_AGGREGATION,
    //         viewtype: 'singleSelect',
    //         displayName: 'Date aggregation',
    //         value: 'day' as datePartDateTrunc,
    //         selectOptions: ['day', 'week', 'month', 'quarter', 'year'] as
    //             | 'day'
    //             | datePartDateTrunc[],
    //     },
    // };
    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: getAllDatePartDateTrunc(),
        },
    };

    settingActiveAllCampaigns: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CAMPAIGN_ACTIVE_ALL,
            viewtype: 'toggle',
            value: 'all campaigns',
            selectOptions: ['all campaigns', "'yesterday' active"],
            // custom onChangeQueryContextCallback so that we can update ATTRIBUTION_CAMPAIGN_ACTIVE column state
            onChangeQueryContextCallback: (
                value: string,
                option: IToggleSettingDashboardItem['options'],
                queryContextService: QueryContextService
            ) => {
                ToggleComponent.onChangeQueryContextCallback(
                    value,
                    option,
                    queryContextService
                );
                const state: IMultipleSelectDatasourceColumnState = {
                    value: {
                        options: [value === 'yesterday active'],
                        filterType: 'include',
                    },
                    isFilterApplied: value === 'yesterday active',
                    isSplitApplied: false,
                };
                queryContextService.changePendingQueryContext({
                    datasourceColumn: {
                        id: 'ATTRIBUTION_CAMPAIGN_ACTIVE',
                        state,
                    },
                });
            },
        },
    };

    settingItemNetGross: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.NET_GROSS,
            viewtype: 'toggle',
            value: 'net',
            selectOptions: ['net', 'gross'],
        },
    };
    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };
    settingRevenueType: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REVENUE_TYPE,
            viewtype: 'toggle',
            value: 'total',
            selectOptions: ['total', 'inapp', 'ad'],
        },
    };

    settingRoasSelectedDayNo: IMultiSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.ROAS_SELECTED_DAY_NO,
            viewtype: 'multiSelect',
            displayName: 'Calendar ROAS days',
            value: this.getInitialContext().settings[
                QueryContextSettingID.ROAS_SELECTED_DAY_NO
            ],
            selectOptions: [
                '1',
                '3',
                '7',
                '14',
                '30',
                '90',
                '180',
                '360',
                '720',
            ].map((v) => ({ value: v, label: v })),
        },
    } as IMultiSelectSettingDashboardItem;

    // CHARTS
    modify_time_campaigns_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.campaigns_charts.modify_time_campaigns_datasource',
        // dashboardItemFactory: DashboardItemFactories['common.modify_time']({
        dashboardItemFactory: new ModifyTimeDashboardItemFactory({
            createdInstanceId:
                'mopa.campaigns_charts.modify_time_campaigns_datasource',
            columnId: 'value',
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.modify_time_campaigns_datasource'
            ].emitter,
    };

    network_warning: IDashboardItemOptionsDataLoaderLink<IAlertDataTextDashboardItemOptions> =
        {
            dashboardItemOptions: {
                component: 'AlertDataTextDashboardItem',
                instanceId: 'mopa.campaigns_charts.network_warning',
                columnName: 'message',
            },
            dataLoaderEmitter:
                this.chartDataLoaders[
                    'mopa.campaigns_charts.campaigns_network_warning'
                ].emitter,
        };

    arpu: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.arpu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.arpu',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Running ARPU',
                chartDocumentation: MopaCampaignsChartsChartDocs.arpu,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter:
                MopaCampaignsChartsComponent.overrideContextInRevenueMetricsGetter(
                    EChartsHelper.createRevenueMetricsGetter({
                        metricId: 'ARPU',
                        metricName: 'Running ARPU',
                    })
                ),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },

                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_arpu'].emitter,
    };

    running_roas: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.roas',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.roas',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Running ROAS',
                chartDocumentation: MopaCampaignsChartsChartDocs.running_roas,
            },
            dashboardName: this.DASHBOARD_NAME,
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            yColumnLabel: 'running_roas',
            metricName: 'Running ROAS',
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_day_no'
            ].emitter,
    };

    dau: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.dau',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'DAU',
                chartDocumentation: MopaCampaignsChartsChartDocs.dau,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricName: 'DAU',
            xColumnLabel: 'date',
            yColumnLabel: 'sum_daily_active_users',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_dau'].emitter,
    };
    revenue: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.revenue',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                chartDocumentation: MopaCampaignsChartsChartDocs.revenue,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricsGetter:
                MopaCampaignsChartsComponent.overrideContextInRevenueMetricsGetter(
                    EChartsHelper.createRevenueMetricsGetter({
                        metricId: 'revenue',
                        metricName: 'Revenue',
                    })
                ),
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_dau'].emitter,
    };
    wavg_arpdau: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.wavg_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.wavg_arpdau',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'ARPDAU',
                chartDocumentation: MopaCampaignsChartsChartDocs.wavg_arpdau,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricsGetter:
                MopaCampaignsChartsComponent.overrideContextInRevenueMetricsGetter(
                    EChartsHelper.createRevenueMetricsGetter({
                        metricId: 'wavg_ARPDAU',
                        metricName: 'ARPDAU',
                    })
                ),
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_dau'].emitter,
    };
    wavg_cr: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.wavg_cr',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.wavg_cr',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Conversion rate',
                subtitle: 'Rate of players who made any purchase',
                chartDocumentation: MopaCampaignsChartsChartDocs.wavg_cr,
            },
            dashboardName: this.DASHBOARD_NAME,
            echartsSeriesType: 'line',
            xColumnLabel: 'date',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            yColumnLabel: 'wavg_conversion_rate',
            metricName: 'Conversion rate',
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_dau'].emitter,
    };
    new_payers: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.new_payers',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.new_payers',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'New payers',
                chartDocumentation: MopaCampaignsChartsChartDocs.new_payers,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'New payers',
            yColumnLabel: 'cnt_first_transaction',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_dau'].emitter,
    };
    new_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.campaigns.chart.new_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.campaigns.chart.new_users',
            dashboardItem: {
                title: 'New users',
                subtitle: 'BI data',
                chartDocumentation: MopaCampaignsChartsChartDocs.new_users,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.revenue_dau'].emitter,
    };

    retention_per_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.retention_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.retention_by_day_no',
            dashboardItem: {
                title: 'Retention by day_no',
                chartDocumentation:
                    MopaCampaignsChartsChartDocs.retention_per_day_no,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'retention',
            metricName: 'Retention',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            // echartsOptionsOverride: (
            //     context: IQueryContext,
            //     echartsOption: EChartsOption,
            //     echartsInstance: ECharts
            // ) => {
            //     echartsOption.yAxis = {
            //         axisLabel: {
            //             formatter:
            //                 EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            //         },
            //     };
            //     echartsOption.tooltip =
            //         EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
            //             xLabelFormatter: (label: any) => `Day no: ${label}`,
            //             valueFormatter:
            //                 EChartsTooltipHelper.tooltipPercentValueFormatter,
            //         });
            // },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.retention_by_day_no']
                .emitter,
    };

    avg_user_lifetime: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.avg_user_lifetime',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.avg_user_lifetime',
            dashboardItem: {
                title: "Average users's lifetime",
                chartDocumentation:
                    MopaCampaignsChartsChartDocs.avg_user_lifetime,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_user_lifetime',
            metricName: "Avg users's lifetime",
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter: () => (v: any) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        v,
                        ' days'
                    ),
            },
            // echartsOptionsOverride: (
            //     context: IQueryContext,
            //     echartsOption: EChartsOption,
            //     echartsInstance: ECharts
            // ) => {
            //     echartsOption.tooltip =
            //         EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
            //             xLabelFormatter: (label: any) => `Day no: ${label}`,
            //             valueFormatter: (v: any) =>
            //                 EChartsTooltipHelper.tooltipNumberValueFormatter(
            //                     v,
            //                     ' days'
            //                 ),
            //         });
            // },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns_charts.retention_by_day_no']
                .emitter,
    };
    // average cpi
    cpi: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.cpi',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.cpi',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);

                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'CPI',
                chartDocumentation: MopaCampaignsChartsChartDocs.cpi,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'CPI',
            yColumnLabel: 'cpi',
            // xColumnLabel: 'date',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };
    // avg cpm
    cpm: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.cpm',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.cpm',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);

                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'CPM',
                chartDocumentation: MopaCampaignsChartsChartDocs.cpm,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'CPM',
            yColumnLabel: 'cpm',
            // xColumnLabel: 'date',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };
    // costs
    costs: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.costs',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.costs',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);

                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Costs',
                chartDocumentation: MopaCampaignsChartsChartDocs.costs,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'Costs',
            yColumnLabel: 'costs',
            // xColumnLabel: 'date',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };
    // installs
    installs: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.installs',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.installs',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);

                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Installs',
                subtitle: 'AppsFlyer data',
                chartDocumentation: MopaCampaignsChartsChartDocs.installs,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'Installs',
            yColumnLabel: 'installs',
            // xColumnLabel: 'date',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };
    // impressions
    impressions: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.impressions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.impressions',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);

                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Impressions',
                chartDocumentation: MopaCampaignsChartsChartDocs.impressions,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'Impressions',
            yColumnLabel: 'impressions',
            // xColumnLabel: 'date',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };
    // reattributions
    reattributions: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.reattributions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.reattributions',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);

                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Reattributions',
                chartDocumentation: MopaCampaignsChartsChartDocs.reattributions,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricName: 'Reattributions',
            yColumnLabel: 'reattributions',
            // xColumnLabel: 'date',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };
    roas_calendar: IDashboardItemDataLoaderLink = {
        id: 'mopa.campaigns_charts.chart.roas_calendar',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.campaigns_charts.chart.roas_calendar',
            dashboardName: this.DASHBOARD_NAME,
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'ROAS',
                chartDocumentation: MopaCampaignsChartsChartDocs.roas_calendar,
            },
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRoasMetricsGetter({
                metricId: 'roas',
                metricName: 'Roas',
            }),
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns_charts.campaigns_metrics_by_date'
            ].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'lnk.mopa.campaigns_charts.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.campaigns_charts.filter.fFirebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
                spltAggregates: [
                    'day',
                    'week',
                    'month',
                    'quarter',
                    'year',
                    'total',
                ],
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'lnk.mopa.campaigns_charts.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'lnk.mopa.campaigns_charts.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'lnk.mopa.campaigns_charts.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.firebase_country'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_traffic_source'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_campaign_name'
                ].emitter,
        },
        ATTRIBUTION_AD: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad',
                    'ATTRIBUTION_AD'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad'
                ].emitter,
        },
        ATTRIBUTION_AD_LENGTH_S: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_ad_length',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_ad_length_s',
                    'ATTRIBUTION_AD_LENGTH_S'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad_length'
                ].emitter,
        },
        ATTRIBUTION_AD_SIZE: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_ad_size',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_ad_size',
                    'ATTRIBUTION_AD_SIZE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad_size'
                ].emitter,
        },
        ATTRIBUTION_AD_ASPECT_RATIO: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_ad_aspect_ratio',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_ad_aspect_ratio',
                    'ATTRIBUTION_AD_ASPECT_RATIO'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad_aspect_ratio'
                ].emitter,
        },
        ATTRIBUTION_AD_TYPE: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_ad_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_ad_type',
                    'ATTRIBUTION_AD_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad_type'
                ].emitter,
        },
        ATTRIBUTION_AD_THEME: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_ad_theme',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_ad_theme',
                    'ATTRIBUTION_AD_THEME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad_theme'
                ].emitter,
        },
        ATTRIBUTION_AD_VERSION: {
            id: 'lnk.mopa.campaigns_charts.filter.attribution_ad_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns_charts.filter.attribution_ad_version',
                    'ATTRIBUTION_AD_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns_charts.filter.attribution_ad_version'
                ].emitter,
        },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaCampaignsChartsComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }
    ngAfterViewInit() {
        console.log({
            msg: 'MopaCampaignsChartsComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const fodDateIC: IDateRangePickerDashboardItem['datasourceColumn'] = {
            id: 'FIREBASE_FIRST_OPEN_DATE',
            state: {
                value: {
                    preset: 'last 30 days',
                    splitAggregate: 'day',
                },
                isFilterApplied: true,
                isSplitApplied: false,
            },
        };

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const trafficSourceIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'ATTRIBUTION_TRAFFIC_SOURCE',
                state: {
                    value: {
                        options: ['Non-Organic'],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            fodDateIC,
            // firebaseAppStoreIC,
            trafficSourceIC,
        ];

        initialContext.settings[
            QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS
        ] =
            QueryContextDefaultSettings[
                QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS
            ]!;
        initialContext.settings['revenueType'] = 'total';
        initialContext.settings[QueryContextSettingID.ROAS_SELECTED_DAY_NO] = [
            '1',
            '3',
            '7',
        ];

        return initialContext;
    }

    /** Return metricsGetter with overriden context */
    static overrideContextInRevenueMetricsGetter(
        metricsGetter: (context: IQueryContext) => Record<string, EChartsMetric>
    ): (context: IQueryContext) => Record<string, any> {
        return (context: IQueryContext) => {
            // deepcopy
            const overridenContext: IQueryContext = JSON.parse(
                JSON.stringify(context)
            );

            overridenContext.settings['showTotalRevenue'] =
                context.settings['revenueType'] === 'total';
            overridenContext.settings['showInappRevenue'] =
                context.settings['revenueType'] === 'inapp';
            overridenContext.settings['showAdRevenue'] =
                context.settings['revenueType'] === 'ad';

            return metricsGetter(overridenContext);
        };
    }
}
